import React, { ChangeEvent, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ADMIN_PAGES_KEYS, NS } from '../../i18n/constants/keys';
import { createSkill, createSubSkill } from '../../utils/api_service/skill_api';
import {
  convertFileToBase64,
  validateQuestionMedia,
} from '../../utils/helpers/fileHandler';
import { addNewSkill } from '../../utils/redux_store/features/skill_reducer';
import { addNewSubSkill } from '../../utils/redux_store/features/subskill_reducer';
import { Question, Skill, SubSkill } from '../../utils/types/assessment_types';
import { SelectOptions } from '../../utils/types/react_select_types';
import { UploadMedia } from '../collapsible_question/upload_media/upload_media';
import { CustomSelect } from '../custom_react_select/custom_select';
import { MinuteSecondsInput } from '../input/minute_seconds_input/minute_seconds_input';
import { TextArea } from '../input/text_area/text_area';
import { TextInput } from '../input/text_input/text_input';

type AddQuestionModelProps = {
  addNewQuestion: (question: Question) => void;
  skillList: SelectOptions[];
  levelList: SelectOptions[];
  jobTitleList: SelectOptions[];
  subSkillList: SubSkill[] | undefined;
  setSkillList: any;
};

export const AddQuestionModal = ({
  addNewQuestion,
  skillList,
  levelList,
  jobTitleList,
  subSkillList,
  setSkillList,
}: AddQuestionModelProps) => {
  const newQuestionState: Question = {
    id: '',
    jobId: '',
    question: '',
    answer: '',
    skillId: '',
    levelId: '',
    subSkill1Id: '',
    subSkill2Id: '',
    subSkill3Id: '',
    questionType: 'Modern',
    questionTheme: '',
    time: 30,
    points: 1,
    fileEncoded: null,
    mediaQuestion: false,
    mediaExtension: null,
    jobTitle: [],
  };
  const { t } = useTranslation(NS.adminPages);
  const AQM = ADMIN_PAGES_KEYS.jobOpenings.addQuestionModal;
  const dispatch = useDispatch();
  const [newQuestion, setNewQuestion] = useState<Question>(newQuestionState);
  const [skills, setSkills] = useState<SelectOptions[]>(skillList || []);

  const returnSubSkills = (parent: string | null | undefined) => {
    let subSkills: SelectOptions[] = [];
    subSkillList?.map((subSkill: SubSkill) => {
      if (subSkill.parentSkillId === parent) {
        subSkills.push({ label: subSkill.subskillName, value: subSkill.id });
      }
    });
    return subSkills;
  };

  const [subSkills1, setSubSkills1] = useState<SelectOptions[]>(() =>
    returnSubSkills(newQuestion.skillId),
  );
  const [subSkills2, setSubSkills2] = useState<SelectOptions[]>(() =>
    returnSubSkills(newQuestion.subSkill1Id),
  );
  const [subSkills3, setSubSkills3] = useState<SelectOptions[]>(() =>
    returnSubSkills(newQuestion.subSkill2Id),
  );

  const questionTypes: SelectOptions[] = [
    { label: 'Traditional', value: 'Traditional' },
    { label: 'Modern', value: 'Modern' },
  ];

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setNewQuestion({
      ...newQuestion,
      [name]: value,
    });
  };

  const handleSelectChange = (selectedValue: SelectOptions, action: any) => {
    if (action.name === 'skillId') {
      selectedValue &&
        setSubSkills1(() => returnSubSkills(selectedValue.value));
      setSubSkills2([]);
      setSubSkills3([]);
      setNewQuestion({
        ...newQuestion,
        [action.name]: selectedValue ? selectedValue.value : null,
        subSkill1Id: '',
        subSkill2Id: '',
        subSkill3Id: '',
      });
    } else if (action.name === 'subSkill1Id') {
      selectedValue &&
        setSubSkills2(() => returnSubSkills(selectedValue.value));
      setSubSkills3([]);
      setNewQuestion({
        ...newQuestion,
        [action.name]: selectedValue ? selectedValue.value : null,
        subSkill2Id: '',
        subSkill3Id: '',
      });
    } else if (action.name === 'subSkill2Id') {
      selectedValue &&
        setSubSkills3(() => returnSubSkills(selectedValue.value));
      setNewQuestion({
        ...newQuestion,
        [action.name]: selectedValue ? selectedValue.value : null,
        subSkill3Id: '',
      });
    } else {
      setNewQuestion({
        ...newQuestion,
        [action.name]: selectedValue ? selectedValue.value : null,
      });
    }
  };

  const handleMultiSelectChange = (
    selectedValue: SelectOptions[],
    action: any,
  ) => {
    let ids: string[] = [];
    selectedValue?.map((selected: SelectOptions) => {
      ids.push(selected.value);
    });

    setNewQuestion({
      ...newQuestion,
      [action.name]: ids,
    });
  };

  const onAddNewSkill = (
    skill: string,
    parent?: string | null,
    level?: string,
  ) => {
    const parentSkillId = parent || null;
    if (parentSkillId && level) {
      createSubSkill(skill, parentSkillId)
        .then((res: any) => (typeof res?.json === 'function' ? res.json() : res))
        .then((response: SubSkill) => {
          dispatch(addNewSubSkill(response));
          const newSubSkill: SelectOptions = {
            label: response.subskillName,
            value: response.id,
          };
          switch (level) {
            case '1':
              setSubSkills1([...(subSkills1 || []), newSubSkill]);
              setNewQuestion({
                ...newQuestion,
                subSkill1Id: response.id,
              });
              break;
            case '2':
              setSubSkills2([...(subSkills2 || []), newSubSkill]);
              setNewQuestion({
                ...newQuestion,
                subSkill2Id: response.id,
              });
              break;
            case '3':
              setSubSkills3([...(subSkills3 || []), newSubSkill]);
              setNewQuestion({
                ...newQuestion,
                subSkill3Id: response.id,
              });
              break;
          }
        });
    } else {
      createSkill(skill)
        .then((res: any) => (typeof res?.json === 'function' ? res.json() : res))
        .then((response: Skill) => {
          const newSkillOption: SelectOptions = {
            label: response.skillName,
            value: response.id,
          };
          dispatch(addNewSkill(newSkillOption));
          setSkills([
            ...skills,
            newSkillOption,
          ]);
          setSkillList([
            ...skills,
            newSkillOption,
          ]);
          setNewQuestion({
            ...newQuestion,
            skillId: response.id,
          });
        });
    }
  };

  const addFileViaInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length) {
      if (validateQuestionMedia(files[0])) {
        convertFileToBase64(files[0]).then((encodedFile: any) => {
          setNewQuestion({
            ...newQuestion,
            fileEncoded: encodedFile as string,
            mediaQuestion: true,
            mediaExtension: files[0].name.split('.').pop() || null,
          });
        });
      }
    }
  };

  const clearMediaFile = () => {
    setNewQuestion({
      ...newQuestion,
      fileEncoded: null,
      mediaQuestion: false,
      mediaExtension: null,
    });
  };

  const selectedJobTitles = jobTitleList?.filter((jobTitle: SelectOptions) =>
    newQuestion.jobTitle?.includes(jobTitle.value),
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextInput
          label={t(AQM.question)}
          name="question"
          value={newQuestion.question}
          type="text"
          onChange={handleChange}
        />
        <TextArea
          label={t(AQM.answer)}
          name="answer"
          value={newQuestion.answer}
          onChange={handleChange}
        />
      </Box>

      <UploadMedia
        question={newQuestion}
        addFileViaInput={addFileViaInput}
        clearMediaFile={clearMediaFile}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        <CustomSelect
          label={t(AQM.skill)}
          name="skillId"
          value={
            skills?.filter((option) => option.value === newQuestion.skillId)[0]
          }
          options={skills}
          onChange={handleSelectChange}
          creatable={true}
          onCreateOption={onAddNewSkill}
        />

        {newQuestion.skillId !== null && newQuestion.skillId !== '' && (
          <CustomSelect
            label={t(AQM.subSkill1)}
            name="subSkill1Id"
            value={
              subSkills1?.filter(
                (option) => option.value === newQuestion.subSkill1Id,
              )[0]
            }
            options={subSkills1}
            onChange={handleSelectChange}
            creatable={true}
            clearable={true}
            onCreateOption={(input) =>
              onAddNewSkill(input, newQuestion.skillId, '1')
            }
          />
        )}

        {newQuestion.subSkill1Id && (
          <CustomSelect
            label={t(AQM.subSkill2)}
            name="subSkill2Id"
            value={
              subSkills2?.filter(
                (option) => option.value === newQuestion.subSkill2Id,
              )[0]
            }
            options={subSkills2}
            onChange={handleSelectChange}
            creatable={true}
            clearable={true}
            onCreateOption={(input) =>
              onAddNewSkill(input, newQuestion.subSkill1Id, '2')
            }
          />
        )}

        {newQuestion.subSkill2Id && (
          <CustomSelect
            label={t(AQM.subSkill3)}
            name="subSkill3Id"
            value={
              subSkills3?.filter(
                (option) => option.value === newQuestion.subSkill3Id,
              )[0]
            }
            options={subSkills3}
            onChange={handleSelectChange}
            creatable={true}
            clearable={true}
            onCreateOption={(input) =>
              onAddNewSkill(input, newQuestion.subSkill2Id, '3')
            }
          />
        )}

        <CustomSelect
          label={t(AQM.level)}
          name="levelId"
          value={
            levelList?.filter((option) => option.value === newQuestion.levelId)[0]
          }
          options={levelList}
          onChange={handleSelectChange}
        />

        <CustomSelect
          label={t(AQM.jobTitle)}
          name="jobTitle"
          value={selectedJobTitles}
          options={jobTitleList}
          onChange={handleMultiSelectChange}
          isMulti={true}
          clearable
          placeholder={t(AQM.selectJobTitles)}
        />

        <CustomSelect
          label={t(AQM.questionType)}
          name="questionType"
          value={
            newQuestion.questionType
              ? questionTypes?.filter(
                  (option) => option.value === newQuestion.questionType,
                )[0]
              : questionTypes[0]
          }
          options={questionTypes}
          onChange={handleSelectChange}
        />

        <MinuteSecondsInput
          label={t(AQM.timerSeconds)}
          name="time"
          value={newQuestion.time}
          onChange={handleChange}
        />

        <TextInput
          label={t(AQM.pointsForQuestion)}
          name="points"
          value={newQuestion.points.toString()}
          type="number"
          onChange={handleChange}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          onClick={() => addNewQuestion(newQuestion)}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            px: 2.5,
            boxShadow: 'none',
          }}
        >
          {t(AQM.addQuestion)}
        </Button>
      </Box>
    </Box>
  );
};
